import { useEffect, useMemo } from 'react';
import type { KeyboardEvent } from 'react';

import { Alignment, Fit, Layout } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { EnumOpenDialog } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';

import { guardRepeatClicks } from './bannerCtaAction';
import styles from './RiveHeader.module.scss';
import { bindTocLobbyBanner, type TocLobbyBannerValues } from './tocLobbyBanner';

interface TocLobbyHeaderProps {
  riveUrl: string;
  values: TocLobbyBannerValues | null;
  bannerClassName?: string;
}

/** The ToC lobby file in place of the lobby banner; a tap opens the ToC dialog. Key it by `riveUrl`. */
const TocLobbyHeader: React.FC<TocLobbyHeaderProps> = observer(({ riveUrl, values, bannerClassName }) => {
  const { uiStore, tournamentStore, tournamentOfChampionsStore } = useStores();
  const layout = useMemo(() => new Layout({ fit: Fit.Cover, alignment: Alignment.Center }), []);
  const { rive, RiveComponent } = useRiveMachine({
    src: riveUrl,
    autoBind: true,
    isTouchScrollEnabled: true,
    layout,
    onLoadError: () => uiStore.markTocLobbyRiveFailed(riveUrl),
  });

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm || !values) return;
    bindTocLobbyBanner(vm, values);
  }, [rive, values]);

  const onTap = useMemo(
    () =>
      guardRepeatClicks(() => {
        void tournamentStore.fetch();
        if (!tournamentOfChampionsStore.canOpenDialog) return;
        uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenTournamentDialog, options: { shown_by: 'click' } });
      }),
    [tournamentStore, tournamentOfChampionsStore, uiStore],
  );
  const isTappable = tournamentOfChampionsStore.canOpenDialog;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onTap();
  };

  return (
    <div
      className={classNames(styles.tocLobby, { [styles.tocLobbyInteractive]: isTappable }, bannerClassName)}
      data-testid="toc-lobby-banner"
      role={isTappable ? 'button' : undefined}
      tabIndex={isTappable ? 0 : undefined}
      onClick={isTappable ? onTap : undefined}
      onKeyDown={isTappable ? onKeyDown : undefined}
    >
      <RiveComponent className={styles.tocLobbyRive} />
    </div>
  );
});

export default TocLobbyHeader;
